import { useId, useState } from "react";
import { authReauthErrorMessage } from "@ridereserv/shared/utils";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/Button";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/authStore";
import { useStepUpStore } from "@/stores/stepUpStore";

/**
 * The step-up re-authentication challenge dialog (issue #1402, porting
 * `apps/web`'s #498 Phase A `StepUpDialog` to the admin console). Mounted
 * ONCE at the app root (see App.tsx) — it renders nothing until `api.ts`'s
 * response interceptor calls `requestStepUp()` on a `428 STEP_UP_REQUIRED`
 * response, at which point `useStepUpStore.isOpen` flips true.
 *
 * Re-confirms the ALREADY-signed-in admin's password via
 * `supabase.auth.signInWithPassword` (the same call LoginPage uses) — this
 * mints a fresh Supabase session with a current `amr` password timestamp,
 * which is exactly the signal the API's step-up gate checks. On success,
 * resolves the pending challenge (the interceptor then deletes the stale
 * `Authorization` header and retries the original request exactly once,
 * picking up the fresh token via the request interceptor). On cancel,
 * rejects the challenge so the interceptor re-surfaces the original 428
 * rather than retrying.
 *
 * This is REQUIRED, not optional: apps/admin is the ONLY client that calls
 * platform-admin routes (there is no mobile carve-out here), and the
 * "recency" policy expires after 15 minutes — without this dialog, routine
 * admin actions (e.g. approving a driver) would hard-fail with no recovery
 * path except a full logout/login.
 *
 * SECURITY: the entered password is held only in local component state for
 * the duration of the submit, is NEVER logged, and is cleared immediately
 * after a successful (or failed) sign-in attempt.
 * @returns The dialog when a challenge is pending, otherwise `null`.
 */
export function StepUpDialog(): React.JSX.Element | null {
  const isOpen = useStepUpStore((s) => s.isOpen);
  const resolveChallenge = useStepUpStore((s) => s.resolveChallenge);
  const cancelChallenge = useStepUpStore((s) => s.cancelChallenge);
  const email = useAuthStore((s) => s.admin?.email);

  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const titleId = useId();
  const passwordFieldId = `${titleId}-password`;

  if (!isOpen) {
    return null;
  }

  const reset = (): void => {
    setPassword("");
    setError(null);
    setIsSubmitting(false);
  };

  const handleClose = (): void => {
    reset();
    cancelChallenge();
  };

  const handleSubmit = async (): Promise<void> => {
    if (!email) {
      // Should be unreachable (the dialog only opens for an authenticated,
      // already-signed-in admin) — fail closed with a clear message rather
      // than calling signInWithPassword with an empty email.
      setError("We couldn't find your account email. Please sign in again.");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });

    if (authError) {
      setIsSubmitting(false);
      setPassword("");
      console.error("Step-up re-authentication error:", authError);
      setError(authReauthErrorMessage(authError));
      return;
    }

    reset();
    resolveChallenge();
  };

  return (
    <Modal
      onClose={handleClose}
      labelledBy={titleId}
      className="max-w-sm"
      overlayClassName="z-[100]"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <div className="mb-4">
          <h2 id={titleId} className="text-base font-semibold text-neutral-900">
            Confirm your password
          </h2>
          <p className="mt-1 text-sm text-neutral-600">
            For your security, please re-enter your password to continue
            {email ? (
              <>
                {" "}
                as <span className="font-medium">{email}</span>
              </>
            ) : null}
            .
          </p>
        </div>

        {error ? (
          <div className="mb-4 rounded-lg bg-destructive-50 p-3 text-sm text-destructive-700">{error}</div>
        ) : null}

        <label htmlFor={passwordFieldId} className="mb-1 block text-sm font-medium text-neutral-700">
          Password
        </label>
        <input
          id={passwordFieldId}
          type="password"
          autoComplete="current-password"
          // A step-up dialog is a modal interruption the admin just triggered
          // (by taking an admin action); Modal's own useEffect also moves
          // focus to the first focusable element, so this just makes that
          // element the password field.
          autoFocus
          required
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
          disabled={isSubmitting}
          className="focus:border-primary-500 focus:ring-primary-500 block w-full rounded-sm border border-neutral-300 px-4 py-2 focus:ring-2 focus:outline-none"
        />

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting} disabled={!password}>
            Confirm
          </Button>
        </div>
      </form>
    </Modal>
  );
}
